import { ConvexError } from "convex/values"
/**
 * Employer identity signals owned by the server: personal inboxes cannot
 * create or use an employer account, and job posting requires the signup
 * email domain to match the company website.
 */

export const EMPLOYER_PERSONAL_EMAIL_MESSAGE =
  "Employers must use a company work email. Personal inboxes like Gmail, Outlook, and Yahoo aren't accepted."

export const JOB_POSTING_EMAIL_DOMAIN_MESSAGE =
  "Your signup email must match your company website domain before you can post."

export const JOB_POSTING_WEBSITE_REQUIRED_MESSAGE =
  "Add your company website so we can match it to your work email before you post."

export const EMPLOYER_EMAIL_UNVERIFIED_MESSAGE =
  "Verify your work email first — enter the code we sent to your inbox."

/** Two-label public suffixes that must not be treated as a company domain. */
const MULTI_PART_PUBLIC_SUFFIXES = new Set([
  "ac.uk",
  "co.in",
  "co.jp",
  "co.nz",
  "co.uk",
  "co.za",
  "com.au",
  "com.br",
  "com.hk",
  "com.mx",
  "com.sg",
  "com.tr",
  "org.uk",
])

const PERSONAL_EMAIL_DOMAINS = new Set([
  "aol.com",
  "duck.com",
  "fastmail.com",
  "gmail.com",
  "gmx.com",
  "gmx.net",
  "googlemail.com",
  "hey.com",
  "hotmail.com",
  "icloud.com",
  "inbox.com",
  "live.com",
  "mac.com",
  "mail.com",
  "me.com",
  "msn.com",
  "outlook.com",
  "pm.me",
  "proton.me",
  "protonmail.com",
  "rediffmail.com",
  "tutanota.com",
  "yahoo.co.in",
  "yahoo.com",
  "yandex.com",
  "ymail.com",
  "zoho.com",
])

export function emailDomain(email: string | undefined | null): string | null {
  if (!email) return null
  const trimmed = email.trim().toLowerCase()
  const at = trimmed.lastIndexOf("@")
  if (at <= 0 || at === trimmed.length - 1) return null
  const domain = trimmed.slice(at + 1).replace(/\.+$/, "")
  return domain.includes(".") ? domain : null
}

export function isPersonalEmailDomain(domain: string | null): boolean {
  if (!domain) return false
  return PERSONAL_EMAIL_DOMAINS.has(domain.toLowerCase())
}

export function isPersonalEmail(email: string | undefined | null): boolean {
  return isPersonalEmailDomain(emailDomain(email))
}

export function companyWebsiteDomain(website: string | undefined | null): string | null {
  if (!website) return null
  const trimmed = website.trim()
  if (!trimmed) return null
  const withProtocol = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`
  try {
    const host = new URL(withProtocol).hostname.trim().toLowerCase().replace(/\.+$/, "")
    if (!host.includes(".")) return null
    return host.replace(/^www\./, "")
  } catch {
    return null
  }
}

function registrableDomain(host: string): string | null {
  const parts = host.split(".").filter(Boolean)
  if (parts.length < 2) return null
  const lastTwo = parts.slice(-2).join(".")
  if (MULTI_PART_PUBLIC_SUFFIXES.has(lastTwo) && parts.length >= 3) {
    return parts.slice(-3).join(".")
  }
  return lastTwo
}

export function workEmailMatchesCompanyDomain(
  email: string | undefined | null,
  website: string | undefined | null,
): boolean {
  if (isPersonalEmail(email)) return false
  const emailHost = emailDomain(email)
  const siteHost = companyWebsiteDomain(website)
  if (!emailHost || !siteHost) return false
  const emailRegistrable = registrableDomain(emailHost)
  const siteRegistrable = registrableDomain(siteHost)
  return Boolean(emailRegistrable && siteRegistrable && emailRegistrable === siteRegistrable)
}

export function assertEmployerWorkEmail(email: string | undefined | null) {
  if (isPersonalEmail(email)) {
    throw new ConvexError(EMPLOYER_PERSONAL_EMAIL_MESSAGE)
  }
}

export function assertCanPostWithWorkEmail(
  profile: { email?: string },
  company: { website?: string },
) {
  if (isPersonalEmail(profile.email)) {
    throw new ConvexError(EMPLOYER_PERSONAL_EMAIL_MESSAGE)
  }
  if (!companyWebsiteDomain(company.website)) {
    throw new ConvexError(JOB_POSTING_WEBSITE_REQUIRED_MESSAGE)
  }
  if (!workEmailMatchesCompanyDomain(profile.email, company.website)) {
    throw new ConvexError(JOB_POSTING_EMAIL_DOMAIN_MESSAGE)
  }
}
